/**
 * Holds quantity steppers at their minimum purchase quantity.
 *
 * UX LAYER ONLY. The order is gated server-side by the min-purchase-quantity Cart &
 * Checkout Validation function in the fireplace-bundles app. See
 * snippets/min-purchase-qty.liquid. This file only stops the customer reaching
 * checkout with a cart that will be rejected.
 *
 * Why this exists rather than just setting `min` in Liquid:
 *
 * theme.js QuantityInput.setQuantityBoundries() rewrites the input's `min` attribute
 * at runtime, and can write NaN into it. Two things then break:
 *
 *   - validateQtyRules() guards on `if (this.input.min)`, and `parseInt(value) <= NaN`
 *     is false, so the minus button never gets disabled.
 *   - onButtonClick() then runs `this.input.value = parseInt(this.input.min)`, and
 *     assigning NaN to a number input empties it — the field goes blank.
 *
 * So the minimum is carried in `data-min-purchase-qty`, which nothing else writes to,
 * and the minus button is intercepted in the capture phase before theme.js sees the
 * click.
 *
 * The theme's own relaxation is preserved: once the cart already holds the minimum,
 * setQuantityBoundries lowers `min` so the customer can top up one at a time. A valid
 * `min` is therefore trusted, and data-min-purchase-qty is only the fallback for when
 * it is unusable.
 */
(() => {
  const MIN_ATTR = 'data-min-purchase-qty';
  const INPUT_SELECTOR = `.quantity__input[${MIN_ATTR}]`;

  const toInteger = (value) => {
    const parsed = parseInt(value, 10);
    return Number.isInteger(parsed) ? parsed : null;
  };

  /** The lowest value this input may hold, or null if it carries no minimum. */
  const floorFor = (input) => {
    const declared = toInteger(input.getAttribute(MIN_ATTR));
    if (declared === null || declared <= 1) return null;

    // Trust a usable runtime min over our own attribute — that is how the theme
    // signals "the cart already covers the minimum, one at a time is fine now".
    const runtime = toInteger(input.min);
    return runtime === null || runtime < 0 ? declared : runtime;
  };

  const stepFor = (input) => {
    const step = toInteger(input.getAttribute('step'));
    return step !== null && step > 0 ? step : 1;
  };

  const valueOf = (input, fallback) => {
    const value = toInteger(input.value);
    return value === null ? fallback : value;
  };

  const inputFor = (button) =>
    button.closest('quantity-input, .quantity')?.querySelector(INPUT_SELECTOR) ?? null;

  /**
   * Keep the minus button's disabled state honest. theme.js sets this too, and gets it
   * wrong when its own `min` is unusable, so re-assert after it has had its turn.
   */
  const syncMinusButton = (input) => {
    const floor = floorFor(input);
    if (floor === null) return;

    const button = input.closest('quantity-input, .quantity')?.querySelector('button[name="minus"]');
    if (button) button.toggleAttribute('disabled', valueOf(input, floor) <= floor);
  };

  const syncAll = () => document.querySelectorAll(INPUT_SELECTOR).forEach(syncMinusButton);

  // Deferred so it lands after theme.js has had its own go at the disabled attribute.
  const defer =
    typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (fn) => setTimeout(fn, 0);
  const syncAllSoon = () => defer(syncAll);

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('button[name="minus"]');
      if (!button) return;

      const input = inputFor(button);
      if (!input) return;

      const floor = floorFor(input);
      if (floor === null) return;

      const value = valueOf(input, floor);
      if (value - stepFor(input) >= floor) {
        // Still above the floor after this click — theme.js can handle it.
        syncAllSoon();
        return;
      }

      // Below the floor. Stop theme.js from seeing the click at all, so it never gets
      // to blank the field or push a rejected quantity to the cart.
      event.preventDefault();
      event.stopPropagation();

      if (value !== floor) {
        input.value = floor;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      syncAllSoon();
    },
    true
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input?.matches?.(INPUT_SELECTOR)) return;

      const floor = floorFor(input);
      if (floor === null) return;

      // Correct a typed-in value before theme.js and cart.js read it, so the cart
      // updates to the minimum rather than to what was typed. Deliberately does not
      // stop propagation.
      if (valueOf(input, floor) < floor) input.value = floor;

      syncAllSoon();
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAll);
  } else {
    syncAll();
  }
})();
